import { Link } from "react-router-dom";

const logoUrl = process.env.PUBLIC_URL + "/new_logo.png";
const shot = (name) => `${process.env.PUBLIC_URL}/screenshots/${name}.png`;

const FEATURES = [
  {
    eyebrow: "A person, not a row",
    title: "Every connection gets a real profile",
    body:
      "A photo, how you met, how close you actually are, the moments you shared, where they have worked and studied. It reads like a dossier, not a contact card — the things you would want to remember before walking into a room with them.",
    image: "profile",
    alt: "A connection profile in Aline: portrait and contact details beside shared moments, career and education",
  },
  {
    eyebrow: "Two sides, one rolodex",
    title: "Your professional life and your personal life stay separate",
    body:
      "The same person can be a coworker and a close friend, with their own notes, their own closeness, and their own place on each map. Professional contacts are discoverable by default. Personal ones are private until you say otherwise.",
    image: "dashboard",
    alt: "The Aline dashboard showing a connection list beside a network map grouped by city",
  },
  {
    eyebrow: "Warm paths, not cold outreach",
    title: "See who can actually introduce you",
    body:
      "Your map groups people by where they live, so the New York crowd sits in one wedge and the Bay Area in another — and you can see at a glance who you know in the city you are moving to. Search across the people your connections know, and get the chain back to whoever can make the introduction.",
    image: "network",
    alt: "Aline's introduction network showing paths from you through your connections",
  },
];

const TEAM = [
  { name: "Evan Cabrera", school: "MIT" },
  { name: "Nick Jung", school: "Cornell" },
  { name: "Matt Friscia", school: "Cornell" },
];

const PRIVACY = [
  {
    title: "Your notes are yours",
    body:
      "Private notes are never readable by the person they are about. That is enforced in the database rules, not just the interface.",
  },
  {
    title: "Nothing is public by default",
    body:
      "Your rolodex lives under your account and nobody else can read it. Only a minimal link between two people is ever shared, and only when you opt in.",
  },
  {
    title: "Phone numbers are a key, not a listing",
    body:
      "A number is used once, to confirm you are adding the right person. It is never shown to them and never published.",
  },
];

function initials(name) {
  return name.split(" ").map((part) => part[0]).join("");
}

export default function Welcome() {
  return (
    <div className="marketing">
      <nav className="marketing-nav">
        <Link to="/" className="nav-brand">
          <img src={logoUrl} alt="Aline" className="logo" />
        </Link>
        <div className="marketing-nav-links">
          <a href="#how">How it works</a>
          <a href="#privacy">Privacy</a>
          <a href="#team">Who we are</a>
        </div>
        <div className="marketing-nav-actions">
          <Link to="/login" className="marketing-link">Sign in</Link>
          <Link to="/signup" className="btn-primary btn-sm">Get started</Link>
        </div>
      </nav>

      <header className="marketing-hero">
        <div className="marketing-hero-copy">
          <p className="eyebrow">A private rolodex for college students</p>
          <h1>
            You will meet a thousand people in four years.
            <span> Remember the ones who matter.</span>
          </h1>
          <p className="marketing-lede">
            Aline is a private digital rolodex. Keep a real profile for everyone you meet, keep
            your professional and personal worlds apart, and find the warm path to the person you
            need — without handing your address book to a social network.
          </p>
          <div className="marketing-cta-row">
            <Link to="/signup" className="btn-primary">Start your rolodex</Link>
            <Link to="/login" className="marketing-link">I already have an account →</Link>
          </div>
          <p className="marketing-fineprint">Free while we are in early access.</p>
        </div>
        <div className="marketing-hero-shot">
          <img
            src={shot("profile")}
            alt="A connection profile in Aline"
            width="1800"
            loading="eager"
          />
        </div>
      </header>

      <section className="marketing-problem">
        <div className="marketing-section-inner">
          <h2>The contacts app was never built for this.</h2>
          <div className="marketing-problem-grid">
            <p>
              A phone number tells you nothing. Six months after a career fair you have a name you
              half recognise, no idea where you met, and no idea whether it would be strange to
              reach out.
            </p>
            <p>
              LinkedIn knows your job history but not that you were roommates. Your group chats
              know you are close but cannot tell you who works where. Neither one is yours.
            </p>
            <p>
              Aline is the private layer in between — the part you would write on the back of a
              business card, if anyone still carried them.
            </p>
          </div>
        </div>
      </section>

      <section className="marketing-features" id="how">
        {FEATURES.map((feature, index) => (
          <article
            key={feature.title}
            className={`marketing-feature${index % 2 === 1 ? " is-reversed" : ""}`}
          >
            <div className="marketing-feature-copy">
              <p className="eyebrow">{feature.eyebrow}</p>
              <h2>{feature.title}</h2>
              <p>{feature.body}</p>
            </div>
            <div className="marketing-feature-shot">
              <img src={shot(feature.image)} alt={feature.alt} loading="lazy" />
            </div>
          </article>
        ))}
      </section>

      <section className="marketing-flow">
        <div className="marketing-section-inner">
          <p className="eyebrow">Adding someone takes one screen</p>
          <h2>Type a name. That is the whole requirement.</h2>
          <p className="marketing-lede">
            Personal connections need nothing but a name. Professional ones ask for a phone number,
            because that is how Aline confirms who someone is and can introduce you later. Everything
            else — the photo, the history, the moments — you fill in on their profile whenever you
            like.
          </p>
          <div className="marketing-flow-shot">
            <img src={shot("add")} alt="Adding a connection to Aline in a single screen" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="marketing-privacy" id="privacy">
        <div className="marketing-section-inner">
          <p className="eyebrow">Privacy</p>
          <h2>Built so that we cannot sell you out later.</h2>
          <div className="marketing-privacy-grid">
            {PRIVACY.map((item) => (
              <div key={item.title} className="marketing-privacy-card">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-team" id="team">
        <div className="marketing-section-inner">
          <p className="eyebrow">Who we are</p>
          <h2>Three students who kept losing track of people.</h2>
          <p className="marketing-lede">
            We built Aline because we kept meeting people worth remembering and had nowhere good to
            put them. It is the tool we wanted in our own first two years.
          </p>
          <ul className="marketing-team-grid">
            {TEAM.map((person) => (
              <li key={person.name} className="marketing-team-card">
                <span className="marketing-team-avatar" aria-hidden="true">
                  {initials(person.name)}
                </span>
                <span className="marketing-team-name">{person.name}</span>
                <span className="marketing-team-school">{person.school}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="marketing-final">
        <div className="marketing-section-inner">
          <h2>Start with the last person you met.</h2>
          <p className="marketing-lede">
            It takes about thirty seconds, and you will be glad you did in about six months.
          </p>
          <Link to="/signup" className="btn-primary">Start your rolodex</Link>
        </div>
      </section>

      <footer className="marketing-footer">
        <div className="marketing-section-inner">
          <img src={logoUrl} alt="Aline" className="logo" />
          <p>A private digital rolodex. Built by students, for students.</p>
          <div className="marketing-footer-links">
            <Link to="/login">Sign in</Link>
            <Link to="/signup">Get started</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
